export interface ExtractedReportData {
  reportTitle: string;
  reportType?: string;
  columns: Array<{ header: string; align?: string; width?: string }>;
  rows: string[][];
  summaryItems: Array<{ label: string; value: string }>;
  filters?: {
    startDate?: string;
    endDate?: string;
    search?: string;
    searchType?: string;
    sortBy?: string;
    partyId?: number;
    saleId?: number;
    itemCode?: string;
    crDr?: string;
  };
}

/**
 * Extracts clean tabular data and summary information from a DOM element
 * rendered on screen for PDF generation and emailing.
 */
export function extractReportDataFromElement(
  element: HTMLElement | null | undefined,
  fallbackTitle: string = "Account Report",
): ExtractedReportData {
  if (!element) {
    return {
      reportTitle: fallbackTitle,
      columns: [{ header: "Information", align: "left" }],
      rows: [["No content found to extract."]],
      summaryItems: [],
    };
  }

  // Clone node to safely inspect without modifying live screen
  const clone = element.cloneNode(true) as HTMLElement;

  // 1. Detect title if not explicitly supplied or if fallback is generic
  let detectedTitle = fallbackTitle;
  const headingEl = clone.querySelector("h1, h2, h3, h4, .report-title, .MuiTypography-h3");
  if (headingEl && headingEl.textContent?.trim()) {
    const text = headingEl.textContent.trim();
    if (text.length > 2 && text.length < 60) {
      detectedTitle = text;
    }
  }

  // 2. Extract key-value metadata before cleaning (e.g., Invoice No, Party, Date, City, Totals)
  const summaryItems: Array<{ label: string; value: string }> = [];
  const filters: ExtractedReportData["filters"] = {};

  // Check common text patterns in container
  // Detached clones have no rendered innerText. Preserve block boundaries and
  // exclude tables so a metadata value can never swallow the ledger.
  const metadata = clone.cloneNode(true) as HTMLElement;
  metadata.querySelectorAll("table, button, script, style, .not-printable").forEach(el => el.remove());
  metadata.querySelectorAll("div, p, h1, h2, h3, h4, h5, h6, br").forEach(el => {
    el.appendChild(document.createTextNode("\n"));
  });
  const fullText = metadata.textContent || "";
  const invoiceMatch = fullText.match(/Invoice\s*(?:No|#)?\s*[:]\s*([^\n\r]+)/i);
  if (invoiceMatch) {
    const val = invoiceMatch[1].trim();
    filters.search = val;
    summaryItems.push({ label: "Invoice No", value: val });
  }

  const partyNameMatch = fullText.match(/Party\s*Name\s*[:]\s*([^\n\r]+)/i);
  if (partyNameMatch) {
    const val = partyNameMatch[1].trim();
    filters.search = val;
    summaryItems.push({ label: "Party Name", value: val });
  }

  const partyCodeMatch = fullText.match(/Party\s*Code\s*[:]\s*([^\n\r]+)/i);
  if (partyCodeMatch) {
    const val = partyCodeMatch[1].trim();
    summaryItems.push({ label: "Party Code", value: val });
  }

  const cityMatch = fullText.match(/City\s*[:]\s*([^\n\r]+)/i);
  if (cityMatch) {
    const val = cityMatch[1].trim();
    if (val && val !== "___" && val !== "N/A") {
      summaryItems.push({ label: "City", value: val });
    }
  }

  const dateMatch = fullText.match(/Date\s*[:]\s*([0-9]{1,2}[/-][0-9]{1,2}[/-][0-9]{2,4}[^\n\r]*)/i);
  if (dateMatch) {
    const val = dateMatch[1].trim();
    filters.startDate = val;
  }

  // 3. Remove non-printable and action elements
  const elementsToRemove = clone.querySelectorAll(
    ".itemCode, .Code, .receipt, .slip, .not-printable, button, .MuiButton-root, .loading-overlay, .loading-state, script, style, [aria-hidden='true']",
  );
  elementsToRemove.forEach((el) => el.remove());

  // 4. Extract tables
  const tables = Array.from(clone.querySelectorAll("table"));

  let columns: Array<{ header: string; align?: string; widthRatio?: number }> = [];
  let rows: string[][] = [];

  if (tables.length === 1) {
    const table = tables[0];
    const extracted = extractSingleTable(table);
    columns = extracted.columns;
    rows = extracted.rows;

    // Check for footer summary rows like total_span or total rows
    if (extracted.totals && extracted.totals.length > 0) {
      extracted.totals.forEach((item) => {
        if (!summaryItems.some((s) => s.label.toLowerCase() === item.label.toLowerCase())) {
          summaryItems.push(item);
        }
      });
    }
  } else if (tables.length === 2) {
    // Two tables side by side (such as Profit & Loss or Cash Book debit/credit)
    const t0 = extractSingleTable(tables[0]);
    const t1 = extractSingleTable(tables[1]);

    // Merge columns side-by-side
    columns = [...t0.columns, ...t1.columns];

    const maxRows = Math.max(t0.rows.length, t1.rows.length);
    for (let i = 0; i < maxRows; i++) {
      const row0 = t0.rows[i] || new Array(t0.columns.length).fill("");
      const row1 = t1.rows[i] || new Array(t1.columns.length).fill("");
      rows.push([...row0, ...row1]);
    }

    // Add totals if available
    [...(t0.totals || []), ...(t1.totals || [])].forEach((t) => {
      summaryItems.push(t);
    });
  } else if (tables.length > 2) {
    // Find table with most rows as primary data table
    let maxTable = tables[0];
    let maxRowCount = 0;
    tables.forEach((t) => {
      const rowCount = t.querySelectorAll("tr").length;
      if (rowCount > maxRowCount) {
        maxRowCount = rowCount;
        maxTable = t;
      }
    });

    const extracted = extractSingleTable(maxTable);
    columns = extracted.columns;
    rows = extracted.rows;
  }

  // 5. Check for bottom summary card items (e.g., Sub Total, Add / Less, Net Balance)
  const summaryMatches = [
    { regex: /Sub\s*Total\s*[:]?\s*(?:Rs\.?)?\s*([0-9,.]+)/i, label: "Sub Total" },
    { regex: /Total\s*Paid\s*[:]?\s*(?:Rs\.?)?\s*([0-9,.]+)/i, label: "Total Paid" },
    { regex: /Total\s*Received\s*[:]?\s*(?:Rs\.?)?\s*([0-9,.]+)/i, label: "Total Received" },
    { regex: /Total\s*Balance\s*[:]?\s*(?:Rs\.?)?\s*([0-9,.]+)/i, label: "Total Balance" },
    { regex: /Net\s*Balance\s*[:]?\s*(?:Rs\.?)?\s*([0-9,.]+)/i, label: "Net Balance" },
    { regex: /Remaining\s*[:]?\s*(?:Rs\.?)?\s*([0-9,.]+)/i, label: "Remaining" },
    { regex: /Difference\s*[:]?\s*(?:Rs\.?)?\s*([0-9,.]+)/i, label: "Difference" },
  ];

  summaryMatches.forEach(({ regex, label }) => {
    const match = fullText.match(regex);
    if (match && !summaryItems.some((s) => s.label.toLowerCase() === label.toLowerCase())) {
      summaryItems.push({ label, value: match[1].trim() });
    }
  });

  clone.querySelectorAll<HTMLElement>("[data-report-label]").forEach(el => {
    const label = el.dataset.reportLabel;
    const value = cleanCellText(el.textContent || "");
    if (label && value) summaryItems.push({ label, value });
  });
  if (!tables.length && summaryItems.length) {
    columns = [{ header: "Description", align: "left" }, { header: "Value", align: "left" }];
    rows = summaryItems.map(item => [item.label, item.value]);
  }

  // Ensure columns are never empty
  if (columns.length === 0) {
    columns = [{ header: "Item Details", align: "left" }];
  }

  return {
    reportTitle: detectedTitle,
    columns,
    rows,
    summaryItems,
    filters,
  };
}

function extractSingleTable(table: HTMLTableElement): {
  columns: Array<{ header: string; align?: string }>;
  rows: string[][];
  totals: Array<{ label: string; value: string }>;
} {
  const trList = Array.from(table.querySelectorAll("tr"));
  if (trList.length === 0) {
    return { columns: [], rows: [], totals: [] };
  }

  // 1. Determine header row
  let headerCells: HTMLElement[] = Array.from(table.querySelectorAll("thead th, thead td"));
  let bodyStartIndex = 0;

  if (headerCells.length === 0) {
    // If no thead, use the first row
    headerCells = Array.from(trList[0].querySelectorAll("th, td"));
    bodyStartIndex = 1;
  }

  // 2. Identify which columns are non-empty across rows
  const allRows = trList.slice(bodyStartIndex).filter(row => !row.closest("thead"));
  const totalCols = headerCells.length;
  const validColIndices: number[] = [];

  for (let c = 0; c < totalCols; c++) {
    const headerText = cleanCellText(headerCells[c]?.innerText || headerCells[c]?.textContent || "");
    let hasAnyData = headerText.length > 0;

    if (!hasAnyData) {
      for (const row of allRows) {
        const cell = row.cells[c];
        if (cell && cleanCellText(cell.innerText || cell.textContent || "").length > 0) {
          hasAnyData = true;
          break;
        }
      }
    }

    if (hasAnyData) {
      validColIndices.push(c);
    }
  }

  // 3. Build Column metadata
  const columns = validColIndices.map((colIdx) => {
    const cell = headerCells[colIdx];
    const header = cleanCellText(cell?.innerText || cell?.textContent || "") || `Col ${colIdx + 1}`;
    const alignAttr = cell?.getAttribute("align")?.toLowerCase();
    let align = "left";

    if (
      alignAttr === "right" ||
      /amount|balance|total|debit|credit|price|rate|cost|dr|cr|qty|quantity/i.test(header)
    ) {
      align = "right";
    } else if (alignAttr === "center" || /status|date|type/i.test(header)) {
      align = "center";
    }

    return { header, align };
  });

  // 4. Extract data rows and any total rows
  const rows: string[][] = [];
  const totals: Array<{ label: string; value: string }> = [];

  allRows.forEach((row) => {
    const isTotalRow =
      row.classList.contains("total_span") ||
      cleanCellText(row.innerText || "").toLowerCase().startsWith("total");

    const rowCells = validColIndices.map((colIdx) => {
      const cell = row.cells[colIdx];
      return cleanCellText(cell?.innerText || cell?.textContent || "");
    });

    // Skip entirely empty rows
    if (rowCells.every((c) => c.length === 0)) {
      return;
    }

    if (isTotalRow) {
      // Find non-empty values to add as total summary item
      const numericVal = rowCells.find((c) => /^[0-9,.-]+$/.test(c.replace(/[^0-9,.-]/g, "")));
      if (numericVal) {
        totals.push({ label: "Total", value: numericVal });
      }
    }

    rows.push(rowCells);
  });

  return { columns, rows, totals };
}

function cleanCellText(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}
